import type { CronJob } from '~/hooks/queries/use-crons';
import type { CronRunStatus } from '~/hooks/queries/use-cron-runs';
import { formatDate } from '~/lib/local-datetime';

/**
 * FRONT-2780: one-time jobs as a first-class kind next to recurring ones.
 * Pure helpers so the Schedule page's split, the Once form and its read-back
 * line are testable without a DOM (the frontend suite has none).
 *
 * The backend owns the truth (`src/cron/one-off.ts`): a one-time job is Done
 * once `completedAt` is set, paused if disabled before it ran, upcoming
 * otherwise. These mirror that reading for display only.
 */

export function isOneOff(cron: Pick<CronJob, 'schedule'>): boolean {
  return Boolean(cron.schedule && typeof cron.schedule === 'object' && cron.schedule.once);
}

export function isDoneOneOff(cron: Pick<CronJob, 'schedule' | 'completedAt'>): boolean {
  return isOneOff(cron) && Boolean(cron.completedAt);
}

export interface ScheduleSplit {
  recurring: CronJob[];
  /** Not run yet — enabled or paused — soonest first. */
  upcoming: CronJob[];
  /** Ran (or failed for good) — most recent first. */
  done: CronJob[];
}

export function splitScheduleList(crons: readonly CronJob[]): ScheduleSplit {
  const recurring: CronJob[] = [];
  const upcoming: CronJob[] = [];
  const done: CronJob[] = [];
  for (const cron of crons) {
    if (!isOneOff(cron)) recurring.push(cron);
    else if (isDoneOneOff(cron)) done.push(cron);
    else upcoming.push(cron);
  }
  const ms = (v?: string) => (v ? new Date(v).getTime() || 0 : 0);
  upcoming.sort((a, b) => ms(a.schedule.runAt) - ms(b.schedule.runAt));
  done.sort((a, b) => ms(b.completedAt) - ms(a.completedAt));
  return { recurring, upcoming, done };
}

/**
 * The toggle a job row offers. A paused one-time job whose time passed can't
 * be resumed (it would fire at once), so it offers a new time instead. A Done
 * one-time job never reaches this — it offers "Schedule again".
 */
export type ToggleAction = 'pause' | 'resume' | 'new-time';

export function toggleActionFor(cron: Pick<CronJob, 'schedule' | 'enabled'>, nowMs: number): ToggleAction {
  if (cron.enabled) return 'pause';
  if (isOneOff(cron)) {
    const at = cron.schedule.runAt ? new Date(cron.schedule.runAt).getTime() : NaN;
    if (!Number.isNaN(at) && at <= nowMs) return 'new-time';
  }
  return 'resume';
}

/** What a Done row says about how it ended. */
export type DoneOutcome = { label: string; tone: 'success' | 'destructive' | 'warning' };

export function doneOutcome(status: CronRunStatus | undefined): DoneOutcome {
  switch (status) {
    case 'failed':
    case 'auth_error':
      return { label: 'Failed', tone: 'destructive' };
    case 'ungrounded':
      return { label: 'Unverified', tone: 'warning' };
    default:
      return { label: 'Done', tone: 'success' };
  }
}

// ---- The Once form ----

export type ScheduleMode = 'once' | 'repeat';

export interface OnceValue {
  /** YYYY-MM-DD, local. */
  date: string;
  /** HH:MM, local, 24h. */
  time: string;
}

const pad = (n: number) => String(n).padStart(2, '0');

export function toOnceValue(d: Date): OnceValue {
  return { date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`, time: `${pad(d.getHours())}:${pad(d.getMinutes())}` };
}

/** A stored runAt as the form's local date and time. */
export function onceValueFromRunAt(runAt: string | undefined): OnceValue {
  const d = runAt ? new Date(runAt) : null;
  if (!d || Number.isNaN(d.getTime())) return { date: '', time: '09:00' };
  return toOnceValue(d);
}

export type OncePreset = 'in-1h' | 'tomorrow-9' | 'next-monday-9';

export const ONCE_PRESETS: Array<{ key: OncePreset; label: string }> = [
  { key: 'in-1h', label: 'In 1 hour' },
  { key: 'tomorrow-9', label: 'Tomorrow 09:00' },
  { key: 'next-monday-9', label: 'Next Monday 09:00' },
];

export function presetValue(preset: OncePreset, now: Date): OnceValue {
  if (preset === 'in-1h') {
    const d = new Date(now.getTime() + 60 * 60_000);
    d.setSeconds(0, 0);
    return toOnceValue(d);
  }
  const d = new Date(now);
  d.setHours(9, 0, 0, 0);
  if (preset === 'tomorrow-9') {
    d.setDate(d.getDate() + 1);
    return toOnceValue(d);
  }
  // Next Monday: strictly after today, so on a Monday it is a week out.
  const daysAhead = ((8 - d.getDay()) % 7) || 7;
  d.setDate(d.getDate() + daysAhead);
  return toOnceValue(d);
}

/** The local instant a date + time names, or null when either is missing or invalid. */
export function onceInstant(v: OnceValue): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v.date);
  const t = /^(\d{1,2}):(\d{2})$/.exec(v.time);
  if (!m || !t) return null;
  const [y, mo, day, h, min] = [Number(m[1]), Number(m[2]), Number(m[3]), Number(t[1]), Number(t[2])];
  if (h > 23 || min > 59) return null;
  const d = new Date(y, mo - 1, day, h, min, 0, 0);
  // Date rolls an impossible day over silently (31 Feb → 3 Mar): refuse it.
  if (Number.isNaN(d.getTime()) || d.getMonth() !== mo - 1 || d.getDate() !== day) return null;
  return d;
}

/** The schedule text the backend parses for a one-time job. */
export function onceExpression(v: OnceValue): string {
  return `on ${v.date} at ${v.time}`;
}

/**
 * The read-back line under the picker, said BEFORE save so a wrong day or
 * zone is caught by the person, not by the job. `blocked` is true exactly
 * when the line explains why Save can't run (one decision, one string).
 */
export type ReadBack = { text: string; tone: 'idle' | 'ok' | 'error'; blocked: boolean };

export function onceReadBack(v: OnceValue, now: Date, timeZone: string): ReadBack {
  if (!v.date) return { text: 'Pick a date and time, or use a preset.', tone: 'idle', blocked: true };
  const at = onceInstant(v);
  if (!at) return { text: 'That date or time is not valid.', tone: 'error', blocked: true };
  if (at.getTime() <= now.getTime()) {
    return { text: 'That time has passed. Pick a time after now.', tone: 'error', blocked: true };
  }
  const sameYear = at.getFullYear() === now.getFullYear();
  // The reader's own date format (FRONT-721: never a hardcoded locale).
  const day = formatDate(at, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
  return { text: `Runs once on ${day}, ${v.time} (${timeZone})`, tone: 'ok', blocked: false };
}

/**
 * Does saving this form change the schedule? A new job, "Schedule again", a
 * switch between Once and Repeats, or a different date, time or text all do.
 * An edit that only touched the name or the task does not — and must not send
 * the schedule, because re-sending it re-arms the job on the server.
 */
export function scheduleEdited(f: {
  isEdit: boolean;
  scheduleAgain: boolean;
  mode: ScheduleMode;
  /** The job's stored one-time date and time; null when it was not one-time. */
  initialOnce: OnceValue | null;
  once: OnceValue;
  /** The job's stored repeat text; null when it was one-time. */
  initialRepeatText: string | null;
  repeatText: string;
}): boolean {
  if (!f.isEdit || f.scheduleAgain) return true;
  if (f.mode === 'once') {
    return !f.initialOnce || f.initialOnce.date !== f.once.date || f.initialOnce.time !== f.once.time;
  }
  return f.initialRepeatText === null || f.initialRepeatText !== f.repeatText;
}
